import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material';
import * as fromAuth from '@app/auth/reducers';
import { LayoutActions } from '@app/core/actions';
import { mapAction, mapConfig, SnackBar } from '@app/core/models/SnackBar';
import { AppInsightService } from '@app/core/services/application-insights';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';

declare global {
    interface Navigator {
      msSaveBlob(blob: any, defaultName?: string): boolean,
      msSaveOrOpenBlob(blob: any, defaultName?: string): boolean
    }
}

@Component({
  selector: 'vt-app',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  errorSnackBarSubscription: Subscription;
  errorSnackBarReference: MatSnackBarRef<any>;

  errorSnackBar$: Observable<SnackBar>;
  showErrorSnackBar$: Observable<boolean>;

  successSnackBarSubscription: Subscription;
  successSnackBarReference: MatSnackBarRef<any>;

  successSnackBar$: Observable<SnackBar>;
  showSuccessSnackBar$: Observable<boolean>;

  infoSnackBarSubscription: Subscription;
  infoSnackBarReference: MatSnackBarRef<any>;

  infoSnackBar$: Observable<SnackBar>;
  showInfoSnackBar$: Observable<boolean>;

  constructor(
    public snackBar: MatSnackBar,
    private readonly store: Store<fromAuth.State>,
    private readonly appInsightService: AppInsightService
  ) {
    this.infoSnackBar$ = this.store.select(fromAuth.getErrorSnackBar);
    this.showErrorSnackBar$ = this.store.select(fromAuth.getShowErrorSnackBar);
    this.successSnackBar$ = this.store.select(fromAuth.getSuccessSnackBar);
    this.showSuccessSnackBar$ = this.store.select(fromAuth.getShowSuccessSnackBar);
    this.infoSnackBar$ = this.store.select(fromAuth.getInfoSnackBar);
    this.showInfoSnackBar$ = this.store.select(fromAuth.getShowInfoSnackBar);
  }

  ngOnInit() {
    this.errorSnackBarSubscription = this.showErrorSnackBar$.subscribe((show: boolean) =>
      show ? this.openErrorSnackBar() : null
    );
    this.successSnackBarSubscription = this.showSuccessSnackBar$.subscribe((show: boolean) =>
      show ? this.openSuccessSnackBar() : null
    );
    this.infoSnackBarSubscription = this.showInfoSnackBar$.subscribe((show: boolean) =>
      show ? this.openInfoSnackBar() : null
    );

    this.appInsightService.logEvent('Portal.Client.Loaded');
  }

  ngOnDestroy() {
   this.errorSnackBarSubscription.unsubscribe();
   this.successSnackBarSubscription.unsubscribe();
   this.infoSnackBarSubscription.unsubscribe();
  }

  onOpenHelp() {
    this.store.dispatch(new LayoutActions.DownloadHelp());
  }

  private openErrorSnackBar() {
    let errorMessage = '';
    this.errorSnackBar$.pipe(first()).subscribe((snackBar: SnackBar) => {
      errorMessage = snackBar.messages.join('\n');
      this.errorSnackBarReference = this.snackBar.open(
        errorMessage,
        mapAction(snackBar.action),
        mapConfig(snackBar.config)
      );
      this.errorSnackBarReference
        .afterDismissed()
        .pipe(first())
        .subscribe(() => {
          this.closeErrorSnackBar();
        });
    });
  }

  private closeErrorSnackBar() {
   this.store.dispatch(new LayoutActions.CloseErrorSnackBar());
  }

  private openSuccessSnackBar() {
    let successMessage = '';
    this.successSnackBar$.pipe(first()).subscribe((snackBar: SnackBar) => {
      successMessage = snackBar.messages.join('\n');
      this.successSnackBarReference = this.snackBar.open(
        successMessage,
        mapAction(snackBar.action),
        mapConfig(snackBar.config)
      );
      this.successSnackBarReference
        .afterDismissed()
        .pipe(first())
        .subscribe(() => {
          this.closeSuccessSnackBar();
        });
    });
  }

  private closeSuccessSnackBar() {
    this.store.dispatch(new LayoutActions.CloseSuccessSnackBar());
  }

  private openInfoSnackBar(): void {
    let infoMessage = '';
    this.infoSnackBar$.pipe(first()).subscribe((snackBar: SnackBar) => {
      infoMessage = snackBar.messages.join('\n');
      this.infoSnackBarReference = this.snackBar.open(
        infoMessage,
        mapAction(snackBar.action),
        mapConfig(snackBar.config)
      );
      this.infoSnackBarReference
        .afterDismissed()
        .pipe(first())
        .subscribe(() => {
          this.closeInfoSnackBar();
        });
    });
  }

  private closeInfoSnackBar(): void {
    this.store.dispatch(new LayoutActions.CloseInfoSnackBar());
  }
}
